import * as React from 'react';

const PIN_FLIP_DURATION_MS = 220;

interface IUsePinFlipAnimationParams {
  /** Индекс закреплённой колонки ЖК или null. Смена значения запускает анимацию. */
  pinnedIndex: number | null;
  scrollRef: React.RefObject<HTMLDivElement | null>;
}

interface IUsePinFlipAnimationResult {
  registerCell(id: string): (node: HTMLDivElement | null) => void;
  handlePinToggleCapture(event: React.MouseEvent<HTMLDivElement>): void;
}

/**
 * FLIP-анимация закрепления колонки: раскладку перестраивает CSS (order + sticky), а хук
 * доигрывает плавный переезд ячеек между старой и новой позициями.
 */
export function usePinFlipAnimation({
  pinnedIndex,
  scrollRef,
}: IUsePinFlipAnimationParams): IUsePinFlipAnimationResult {
  const cellRefs = React.useRef(new Map<string, HTMLDivElement>());
  const firstLeftsRef = React.useRef(new Map<string, number>());
  const pendingFlipRef = React.useRef(false);

  const registerCell = React.useCallback(
    (id: string) => (node: HTMLDivElement | null) => {
      if (node) {
        cellRefs.current.set(id, node);
      } else {
        cellRefs.current.delete(id);
      }
    },
    [],
  );

  // Клик по кнопке фиксации ловим в фазе перехвата — до ре-рендера, — и запоминаем текущие левые
  // границы ячеек («First»). Ловим на контейнере, т.к. кнопки приходят внутри opaque-колонок.
  // Снимок именно в момент клика важен: горизонтальный скролл не вызывает ре-рендер, поэтому
  // «прошлые» позиции из предыдущего рендера были бы неактуальны.
  const handlePinToggleCapture = React.useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement | null;

    if (!target || !target.closest('[data-testid="newbuilding-pin-button"]')) {
      return;
    }

    const firstLefts = new Map<string, number>();

    cellRefs.current.forEach((node, id) => {
      firstLefts.set(id, node.getBoundingClientRect().left);
    });

    firstLeftsRef.current = firstLefts;
    pendingFlipRef.current = true;
  }, []);

  // После перестроения раскладки считаем «Last», ставим обратный сдвиг и плавно доезжаем к 0.
  React.useLayoutEffect(() => {
    if (!pendingFlipRef.current) {
      return undefined;
    }

    pendingFlipRef.current = false;

    // Сбрасываем возможный незавершённый transform от предыдущей анимации, чтобы Last был честным.
    cellRefs.current.forEach(node => {
      node.style.transition = '';
      node.style.transform = '';
    });

    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      return undefined;
    }

    const animatedNodes: HTMLDivElement[] = [];

    cellRefs.current.forEach((node, id) => {
      const firstLeft = firstLeftsRef.current.get(id);

      if (firstLeft === undefined) {
        return;
      }

      const dx = firstLeft - node.getBoundingClientRect().left;

      if (Math.abs(dx) < 0.5) {
        return;
      }

      node.style.transition = 'none';
      node.style.transform = `translateX(${dx}px)`;
      animatedNodes.push(node);
    });

    if (animatedNodes.length === 0) {
      return undefined;
    }

    // Форсируем reflow, чтобы инвертированный сдвиг применился до включения перехода.
    void scrollRef.current?.offsetWidth;

    const rafId = window.requestAnimationFrame(() => {
      animatedNodes.forEach(node => {
        node.style.transition = `transform ${PIN_FLIP_DURATION_MS}ms ease`;
        node.style.transform = 'translateX(0px)';
      });
    });

    const cleanups = animatedNodes.map(node => {
      const handleEnd = () => {
        node.style.transition = '';
        node.style.transform = '';
        node.removeEventListener('transitionend', handleEnd);
      };

      node.addEventListener('transitionend', handleEnd);

      return () => node.removeEventListener('transitionend', handleEnd);
    });

    return () => {
      window.cancelAnimationFrame(rafId);
      cleanups.forEach(cleanup => cleanup());
    };
  }, [pinnedIndex, scrollRef]);

  return {
    handlePinToggleCapture,
    registerCell,
  };
}
